import { Injectable, inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { RoleList } from 'app/core/enums';
import { RbacPermissionService } from 'app/core/rbac/rbac-permission.service';
import { SlateEntityExtractor } from './slate-entity.extractor';
import { SLATE_INTENTS } from './slate-intent.registry';
import {
    findGuideAnyAudience,
    findRouteGuide,
} from './slate-route-guide';
import { isHowToQuestion, isOutOfScope } from './slate-scope.guard';
import { extractOpenCommand } from './slate-open.parse';
import { extractFilmLookup } from './slate-lookup.parse';
import { extractUserLookup } from './slate-user.parse';
import {
    extractCompareDateRanges,
    isCompareAsk,
} from './slate-compare.parse';
import { extractModerationAsk } from './slate-moderation.parse';
import {
    isFeedbackOrConfusionAsk,
    withHelpChips,
} from './slate-support.pure';
import { roleSuggestionPrompts, roleSubtitle } from './slate-role-prompts';
import {
    intentIdForMetricFocus,
    scoreKeywordList,
} from './slate-nlp.pure';
import {
    bareQueryClarifyPack,
    detectBareQuery,
    detectNonEnglishAsk,
    nonEnglishClarifyPack,
} from './slate-clarify.pure';
import { SlateSessionService } from './slate-session.service';
import {
    SlateMatchedIntent,
    SlateParseResult,
    SlateAudience,
    SlateMetricFocus,
} from './slate.types';

@Injectable({ providedIn: 'root' })
export class SlateIntentEngine {
    private readonly _auth = inject(AuthService);
    private readonly _rbac = inject(RbacPermissionService);
    private readonly _session = inject(SlateSessionService);
    private readonly _extractor = new SlateEntityExtractor();

    parse(text: string): SlateParseResult {
        const raw = text.trim();
        const audience = this.audience();
        const thinking: string[] = ['Reading your question…'];

        if (!raw) {
            return {
                kind: 'out_of_scope',
                entities: this._extractor.extract('help', { requireDate: false }),
                thinking: [...thinking, 'Empty message — nothing to run.'],
                message: 'Type a Studio question to get started.',
            };
        }

        // Other language → gentle English redirect (before other routing)
        if (detectNonEnglishAsk(raw)) {
            const pack = nonEnglishClarifyPack(audience);
            thinking.push('Message looks non-English — asking for English.');
            return {
                kind: 'clarify',
                entities: this._extractor.extract(raw, { requireDate: false }),
                thinking,
                clarifyTitle: pack.title,
                message: pack.body,
                promptChips: pack.chips,
            };
        }

        // Wrong data / confused → Help & support (person escape hatch)
        if (isFeedbackOrConfusionAsk(raw)) {
            thinking.push('User needs support / reported confusion.');
            return {
                kind: 'intent',
                intentId: 'help_support',
                label: 'Help & support',
                entities: this._extractor.extract(raw, { requireDate: false }),
                thinking,
                rbacPage: undefined,
                message:
                    'If Talkie’s answer looks wrong or unclear, connect with support below. You can also retry with a simpler English question.',
            };
        }

        const guideForRole = findRouteGuide(raw, audience);
        const anyGuide = findGuideAnyAudience(raw);
        const howTo = isHowToQuestion(raw);

        const openEarly = extractOpenCommand(raw);
        if (openEarly) {
            thinking.push(
                `Open ${openEarly.kind === 'unknown' ? 'title' : openEarly.kind}: "${openEarly.title}"${openEarly.contentId ? ` id=${openEarly.contentId}` : ''} (${openEarly.mode})`
            );
            thinking.push('Checking permissions…');
            return {
                kind: 'intent',
                intentId: 'open_content',
                label: 'Open film or series',
                entities: {
                    ...this._extractor.extract(raw, { requireDate: false }),
                    filmTitleQuery: openEarly.title,
                    filmId: openEarly.contentId,
                    seriesId:
                        openEarly.kind === 'webseries'
                            ? openEarly.contentId
                            : undefined,
                    openMode: openEarly.mode,
                    contentKind: openEarly.kind,
                },
                thinking,
                rbacPage: undefined,
            };
        }

        const userEarly = extractUserLookup(raw);
        if (userEarly) {
            if (audience === 'filmmaker') {
                thinking.push('User directory is admin-only.');
                return {
                    kind: 'out_of_role',
                    entities: this._extractor.extract(raw, {
                        requireDate: false,
                    }),
                    thinking,
                    message:
                        'User search (viewers, filmmakers, partners, curators) is available to Studio admins. As a filmmaker you can ask about your films, series, wallet, and uploads.',
                };
            }
            thinking.push(
                `User lookup ${userEarly.queryKind}: “${userEarly.query}” (${userEarly.userType})`
            );
            thinking.push('Checking permissions…');
            return {
                kind: 'intent',
                intentId: 'user_lookup',
                label: 'Find user',
                entities: {
                    ...this._extractor.extract(raw, { requireDate: false }),
                    userQuery: userEarly.query,
                    userQueryKind: userEarly.queryKind,
                    userListType:
                        userEarly.userType === 'ANY'
                            ? undefined
                            : userEarly.userType,
                    userOpen: userEarly.open,
                    openMode: userEarly.openMode,
                },
                thinking,
                rbacPage: undefined,
            };
        }

        const lookupEarly = extractFilmLookup(raw);
        if (lookupEarly) {
            thinking.push(
                `Content lookup “${lookupEarly.title}” · ${lookupEarly.focuses.join(', ')} (${lookupEarly.kind})`
            );
            thinking.push('Checking permissions…');
            return {
                kind: 'intent',
                intentId: 'film_lookup',
                label: 'Content details',
                entities: {
                    ...this._extractor.extract(raw, { requireDate: false }),
                    filmTitleQuery: lookupEarly.title,
                    detailFocus: lookupEarly.focuses,
                    contentKind: lookupEarly.kind,
                },
                thinking,
                rbacPage: undefined,
            };
        }

        // Approve / reject / convert / ready-for-approve (admin moderation)
        const moderation = extractModerationAsk(raw);
        if (moderation) {
            if (moderation.kind === 'ready_queue') {
                thinking.push('Films ready for approval (Submitted list).');
                thinking.push('Checking permissions…');
                const entities = this._extractor.extract(raw, {
                    requireDate: false,
                });
                entities.status = 'Submitted';
                // Do not clip approval queue by default “last 30 days”
                if (entities.dateRange?.inferredDefault) {
                    entities.dateRange = undefined;
                }
                if (!entities.limit || entities.limit < 10) {
                    entities.limit = 10;
                }
                return {
                    kind: 'intent',
                    intentId: 'film_list',
                    label: 'Films ready for approval',
                    entities,
                    thinking,
                    rbacPage: undefined,
                };
            }
            if (audience === 'filmmaker') {
                thinking.push('Approve / convert actions are admin-only.');
                return {
                    kind: 'out_of_role',
                    entities: this._extractor.extract(raw, {
                        requireDate: false,
                    }),
                    thinking,
                    message:
                        'Approve, reject, and start conversion are Studio admin actions. As a filmmaker you can check your film status or open My Films.',
                };
            }
            thinking.push(
                `Moderation: ${moderation.kind}${moderation.title ? ` · “${moderation.title}”` : ''}`
            );
            thinking.push('Checking permissions…');
            return {
                kind: 'intent',
                intentId: 'film_moderation',
                label:
                    moderation.kind === 'convert_advice'
                        ? 'Start film conversion'
                        : moderation.kind === 'reject_advice'
                          ? 'Reject / decline film'
                          : 'Approve film guidance',
                entities: {
                    ...this._extractor.extract(raw, { requireDate: false }),
                    filmTitleQuery: moderation.title,
                    moderationAction: moderation.kind,
                },
                thinking,
                rbacPage: undefined,
            };
        }

        // Bare name / phone / email → ask what to do (non-tech friendly)
        const bareEarly = detectBareQuery(raw);
        if (bareEarly) {
            const pack = bareQueryClarifyPack(bareEarly, audience);
            thinking.push(
                `Bare ${bareEarly.kind} “${bareEarly.value}” — clarifying.`
            );
            return {
                kind: 'clarify',
                entities: {
                    ...this._extractor.extract(raw, { requireDate: false }),
                    ...(bareEarly.kind === 'title'
                        ? { filmTitleQuery: bareEarly.value }
                        : {
                              userQuery: bareEarly.value,
                              userQueryKind: bareEarly.kind,
                          }),
                },
                thinking,
                clarifyTitle: pack.title,
                message: pack.body,
                promptChips: pack.chips,
            };
        }

        if (isOutOfScope(raw) && !guideForRole && anyGuide.score < 2) {
            thinking.push('This looks outside Studio scope.');
            return {
                kind: 'out_of_scope',
                entities: this._extractor.extract(raw, { requireDate: false }),
                thinking,
                message:
                    'Talkie only helps with ABC Talkies Studio — analytics, lists, reports, wallet, and how-to. Ask “Help and support” for Email, WhatsApp, or Call.',
            };
        }

        const followLimit = this._extractor.extractLimitOnly(raw);
        const followDate = this._extractor.extractDateRange(raw.toLowerCase());
        const ctx = this._session.context;
        const metricOnlyFollow =
            !!ctx.lastIntentId &&
            /^(show|give|get|display)\s+/i.test(raw) &&
            raw.split(/\s+/).length <= 8;
        const looksLikeFollowUp =
            !!ctx.lastIntentId &&
            (/^(same|again|retry)\b/i.test(raw) ||
                /^(make\s+it\s+)?top\s+\d+\b/i.test(raw) ||
                metricOnlyFollow ||
                (/^(last|this|previous|from|between|on)\b/i.test(raw) &&
                    raw.split(/\s+/).length <= 10));

        if (looksLikeFollowUp && ctx.lastIntentId && ctx.lastEntities) {
            const fresh = this._extractor.extract(raw, { requireDate: false });
            const entities = {
                ...ctx.lastEntities,
                ...(followLimit ? { limit: followLimit } : {}),
                ...(followDate ? { dateRange: followDate } : {}),
            };
            // Metric / chip follow-up: replace focus; do NOT keep old top-table dump
            if (
                metricOnlyFollow ||
                fresh.metricFocus !== 'summary' ||
                fresh.wantAllMetrics
            ) {
                entities.metricFocus = fresh.metricFocus;
                entities.wantAllMetrics = fresh.wantAllMetrics;
                entities.wantTopTable = !!fresh.wantTopTable;
            }
            if (fresh.wantTopTable) {
                entities.wantTopTable = true;
            }
            if (fresh.wantAllMetrics) {
                entities.wantAllMetrics = true;
                entities.metricFocus = 'all';
                // all metrics ≠ ranking table unless user also said top
                if (!fresh.wantTopTable) {
                    entities.wantTopTable = false;
                }
            }
            // New film title in follow-up → re-resolve id
            if (fresh.filmTitleQuery) {
                entities.filmTitleQuery = fresh.filmTitleQuery;
                entities.filmId = undefined;
                entities.filmTitle = undefined;
            }
            // Keep previous date if chip had none
            if (!followDate && ctx.lastEntities.dateRange) {
                entities.dateRange = ctx.lastEntities.dateRange;
            }
            thinking.push(`Follow-up on ${ctx.lastLabel || ctx.lastIntentId}`);
            thinking.push(this.entitySummary(entities));
            thinking.push('Checking permissions…');
            return {
                kind: 'follow_up',
                intentId: ctx.lastIntentId,
                label: ctx.lastLabel,
                entities,
                thinking,
                rbacPage: ctx.lastRbacPage,
            };
        }

        const entities = this._extractor.extract(raw);
        thinking.push(this.entitySummary(entities));

        // Compare two days → same analytics intent, two ranges
        if (isCompareAsk(raw)) {
            const cmp = extractCompareDateRanges(raw);
            if (cmp) {
                entities.dateRange = cmp.a;
                entities.compareDateRange = cmp.b;
                thinking.push(
                    `Compare ${cmp.a.label} vs ${cmp.b.label}`
                );
                thinking.push('Checking permissions…');
                return {
                    kind: 'intent',
                    intentId: 'film_analytics',
                    label: 'Compare film metrics',
                    entities,
                    thinking,
                    rbacPage: undefined,
                };
            }
        }

        // How-to only → guide. Do NOT steal live wallet/list asks via keyword score.
        if (guideForRole && howTo) {
            thinking.push(`Detected: help · ${guideForRole.title}`);
            thinking.push('Checking permissions…');
            return {
                kind: 'intent',
                intentId: 'route_guide',
                label: guideForRole.title,
                entities: { ...entities, guideId: guideForRole.id },
                thinking,
                rbacPage: guideForRole.rbacPage,
                guideId: guideForRole.id,
            };
        }

        if (
            howTo &&
            !guideForRole &&
            anyGuide.guide &&
            anyGuide.guide.audience !== 'both' &&
            anyGuide.guide.audience !== audience
        ) {
            thinking.push('Help topic found for a different Studio role.');
            return {
                kind: 'out_of_role',
                entities,
                thinking,
                message:
                    anyGuide.guide.roleNote ||
                    `"${anyGuide.guide.title}" is for the ${anyGuide.guide.audience} portal. Your current role is ${audience}.`,
            };
        }

        const scored = this.scoreIntents(raw, audience, entities.metricFocus);

        if (guideForRole && howTo && (!scored.length || anyGuide.score >= scored[0].score)) {
            thinking.push(`Detected: help · ${guideForRole.title}`);
            return {
                kind: 'intent',
                intentId: 'route_guide',
                label: guideForRole.title,
                entities: { ...entities, guideId: guideForRole.id },
                thinking,
                rbacPage: guideForRole.rbacPage,
                guideId: guideForRole.id,
            };
        }

        if (!scored.length) {
            if (guideForRole) {
                thinking.push(`Detected: help · ${guideForRole.title}`);
                return {
                    kind: 'intent',
                    intentId: 'route_guide',
                    label: guideForRole.title,
                    entities: { ...entities, guideId: guideForRole.id },
                    thinking,
                    rbacPage: guideForRole.rbacPage,
                    guideId: guideForRole.id,
                };
            }
            const bareLate = detectBareQuery(raw);
            if (bareLate) {
                const pack = bareQueryClarifyPack(bareLate, audience);
                thinking.push(
                    `Unmapped bare ${bareLate.kind} — clarifying.`
                );
                return {
                    kind: 'clarify',
                    entities,
                    thinking,
                    clarifyTitle: pack.title,
                    message: pack.body,
                    promptChips: pack.chips,
                };
            }
            thinking.push('Could not map to a Studio action.');
            return {
                kind: 'clarify',
                entities,
                thinking,
                clarifyTitle: 'Please tell me a bit more',
                message:
                    'I am not sure what you need. Please ask in simple English — for example open a film, check status, find a user, or see views. Tap an example below.',
                promptChips: withHelpChips(
                    audience === 'filmmaker'
                        ? [
                              'Show my films',
                              'How do I upload a film?',
                              'My film views last 7 days',
                              'Where is my wallet?',
                          ]
                        : [
                              'Open film "Movie Name" in view mode',
                              'What is the status of "Movie Name"?',
                              'Which movies are ready for approve?',
                              'Should I approve "Movie Name"?',
                              'How to start film conversion?',
                              'Who is mobile 9876543210?',
                              'Film views last 7 days',
                              'How do I upload a film?',
                          ]
                ),
            };
        }

        const top = scored[0];
        const second = scored[1];
        if (second && top.score - second.score < 0.75 && second.score >= 1.5) {
            thinking.push('Multiple matches — need a quick clarification.');
            return {
                kind: 'clarify',
                entities,
                thinking,
                candidates: scored.slice(0, 3).map((s) => s.intent),
            };
        }

        if (!this.allowedForAudience(top.intent.audience, audience)) {
            thinking.push('Blocked — outside your role.');
            return {
                kind: 'out_of_role',
                intentId: top.intent.id,
                label: top.intent.label,
                entities,
                thinking,
                message: `"${top.intent.label}" is not available for your role.`,
            };
        }

        if (
            audience === 'admin' &&
            top.intent.rbacPage &&
            this._rbac.isLoaded() &&
            !this._rbac.can(top.intent.rbacPage, 'read')
        ) {
            thinking.push('Blocked — missing page permission.');
            return {
                kind: 'out_of_role',
                intentId: top.intent.id,
                label: top.intent.label,
                entities,
                thinking,
                rbacPage: top.intent.rbacPage,
                message: `You do not have access to ${top.intent.label}. Ask an admin for permission.`,
            };
        }

        thinking.push(
            `Detected: ${top.intent.label} · ${entities.dateRange?.label || 'no date'} · top ${entities.limit}`
        );
        thinking.push('Checking permissions…');
        thinking.push('Fetching live Studio data…');

        return {
            kind: 'intent',
            intentId: top.intent.id,
            label: top.intent.label,
            entities,
            thinking,
            rbacPage: top.intent.rbacPage,
        };
    }

    audience(): 'admin' | 'filmmaker' {
        const role = this._auth.currentUserRole;
        return role === RoleList.FilmMaker ? 'filmmaker' : 'admin';
    }

    suggestions(): string[] {
        return roleSuggestionPrompts(this._auth.currentUserRole);
    }

    suggestionSubtitle(): string {
        return roleSubtitle(this._auth.currentUserRole);
    }

    private scoreIntents(
        text: string,
        audience: 'admin' | 'filmmaker',
        metricFocus?: SlateMetricFocus
    ): { intent: SlateMatchedIntent; score: number }[] {
        const t = text.toLowerCase();
        const results: { intent: SlateMatchedIntent; score: number }[] = [];
        const metricBoostId = metricFocus
            ? intentIdForMetricFocus(metricFocus)
            : null;

        for (const def of SLATE_INTENTS) {
            if (def.id === 'route_guide') continue;
            if (!this.allowedForAudience(def.audience, audience)) continue;
            // Keyword-only match without a real parse = early-steal class bugs
            if (!this.extractorSupportsIntent(text, def.id)) continue;

            let score = scoreKeywordList(t, def.keywords, def.weight ?? 1);

            // Vernacular metric wording still routes to the right intent
            if (metricBoostId && def.id === metricBoostId) {
                score = Math.max(score, 12);
            }

            if (score > 0) {
                results.push({
                    score,
                    intent: {
                        id: def.id,
                        label: def.label,
                        audience: def.audience,
                        rbacPage: def.rbacPage,
                    },
                });
            }
        }

        return results.sort((a, b) => b.score - a.score);
    }

    /** open / user / film_lookup must parse — never score-only. */
    private extractorSupportsIntent(text: string, intentId: string): boolean {
        if (intentId === 'open_content') {
            return !!extractOpenCommand(text);
        }
        if (intentId === 'user_lookup') {
            return !!extractUserLookup(text);
        }
        if (intentId === 'film_lookup') {
            return !!extractFilmLookup(text);
        }
        return true;
    }

    private allowedForAudience(
        intentAudience: SlateAudience,
        user: 'admin' | 'filmmaker'
    ): boolean {
        if (intentAudience === 'both') return true;
        return intentAudience === user;
    }

    private entitySummary(entities: {
        dateRange?: { label: string; inferredDefault?: boolean };
        limit: number;
        metricFocus?: string;
        wantTopTable?: boolean;
        filmTitle?: string;
        filmTitleQuery?: string;
    }): string {
        const dateBit = entities.dateRange
            ? entities.dateRange.inferredDefault
                ? `No date found — using ${entities.dateRange.label}`
                : `Date range: ${entities.dateRange.label}`
            : 'No date range';
        const focusBit = entities.metricFocus
            ? ` · focus ${entities.metricFocus.replace(/_/g, ' ')}`
            : '';
        const topBit = entities.wantTopTable ? ` · top table` : '';
        const filmBit = entities.filmTitle
            ? ` · film ${entities.filmTitle}`
            : entities.filmTitleQuery
              ? ` · film search ${entities.filmTitleQuery}`
              : '';
        return `${dateBit} · limit ${entities.limit}${focusBit}${topBit}${filmBit}`;
    }
}

